import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  subscribeInFlightRequests,
  cancelInFlightRequest,
  retryInFlightRequest,
} from 'app/utils/utils';

// Shows a user-friendly snackbar when an API request has been running for "too long",
// with Cancel / Retry actions.
//
// This intentionally provides *user control* and avoids the "spinner forever" problem.
const LongRequestNotifier = ({
  longRequestWarningMs = 10000,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [requests, setRequests] = useState([]);

  // requestId -> snackbarKey
  const shownRef = useRef(new Map());

  useEffect(() => {
    const unsubscribe = subscribeInFlightRequests((snapshot) => {
      setRequests(snapshot);
    });
    return unsubscribe;
  }, []);

  // Periodically check durations so the warning appears even if snapshot isn't changing.
  useEffect(() => {
    const timer = setInterval(() => {
      // trigger re-render to re-evaluate durations
      setRequests((prev) => [...prev]);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const activeIds = useMemo(() => new Set(requests.map((r) => r.id)), [requests]);

  // Close snackbars for requests that completed.
  useEffect(() => {
    for (const [id, snackKey] of shownRef.current.entries()) {
      if (!activeIds.has(id)) {
        closeSnackbar(snackKey);
        shownRef.current.delete(id);
      }
    }
  }, [activeIds, closeSnackbar]);

  useEffect(() => {
    const now = Date.now();

    for (const r of requests) {
      if (r?.suppressLongRequestWarning) continue;
      const ageMs = now - (r.startedAt || now);
      if (ageMs < longRequestWarningMs) continue;
      if (shownRef.current.has(r.id)) continue;

      const label = r.which || `${r.method || 'REQ'} ${r.url || ''}`;
      const ageSec = Math.max(1, Math.floor(ageMs / 1000));

      const snackKey = enqueueSnackbar(`Still loading (${ageSec}s): ${label}`, {
        variant: 'warning',
        persist: true,
        action: (key) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                cancelInFlightRequest(r.id);
                closeSnackbar(key);
              }}
            >
              Cancel
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                retryInFlightRequest(r.id);
                closeSnackbar(key);
              }}
            >
              Retry
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => closeSnackbar(key)}
            >
              Dismiss
            </Button>
          </Stack>
        ),
      });

      shownRef.current.set(r.id, snackKey);
    }
  }, [requests, longRequestWarningMs, enqueueSnackbar, closeSnackbar]);

  return null;
};

export default LongRequestNotifier;
